export type PttSidecarProvider = "aws" | "speaches" | "voicebox";

export type PttSidecarConfig = {
  baseUrl: string;
  provider: PttSidecarProvider;
  sttModel: string;
  ttsModel: string;
  ttsEngine?: string;
  ttsProfileName?: string;
  sttPrompt?: string;
  sampleRateHz?: number;
};

export class SidecarError extends Error {}

const profileIdCache = new Map<string, string>();

export function parseSseStatusPayload(body: string): { status?: string; error?: string | null } {
  const lines = body.split("\n").filter((line) => line.startsWith("data: "));
  const last = lines.at(-1);
  if (!last) return {};
  return JSON.parse(last.slice(6)) as { status?: string; error?: string | null };
}

export function resolvePttProvider(
  baseUrl: string,
  explicit: string | undefined,
): PttSidecarProvider {
  if (explicit === "aws" || explicit === "voicebox" || explicit === "speaches") return explicit;
  if (baseUrl.includes("/api/voice/aws")) return "aws";
  if (
    baseUrl.includes("/voicebox") ||
    baseUrl.includes(":17493") ||
    baseUrl.includes(":17600")
  ) {
    return "voicebox";
  }
  return "speaches";
}

export function resolveWebSocketUrl(baseUrl: string, path: string): string {
  const joined = `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
  if (/^https?:\/\//i.test(joined)) {
    return joined.replace(/^http/i, "ws");
  }
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}${joined.startsWith("/") ? joined : `/${joined}`}`;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function playAudioBuffer(arrayBuf: ArrayBuffer): Promise<void> {
  const audioCtx = new AudioContext();
  const decoded = await audioCtx.decodeAudioData(arrayBuf.slice(0));
  const source = audioCtx.createBufferSource();
  source.buffer = decoded;
  source.connect(audioCtx.destination);
  source.start();
  await new Promise<void>((resolve) => {
    source.onended = () => resolve();
  });
  await audioCtx.close();
}

async function ensureVoiceboxProfile(config: PttSidecarConfig): Promise<string> {
  const profileName = config.ttsProfileName ?? "og-orm-ptt";
  const cacheKey = `${config.baseUrl}::${profileName}`;
  const cached = profileIdCache.get(cacheKey);
  if (cached) return cached;

  const listRes = await fetch(`${config.baseUrl}/profiles`);
  if (!listRes.ok) {
    throw new SidecarError(`Voicebox profiles failed (${listRes.status})`);
  }
  const profiles = (await listRes.json()) as Array<{ id: string; name: string }>;
  const existing = profiles.find((p) => p.name === profileName);
  if (existing) {
    profileIdCache.set(cacheKey, existing.id);
    return existing.id;
  }

  const createRes = await fetch(`${config.baseUrl}/profiles`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: profileName,
      language: "en",
      voice_type: "preset",
      preset_engine: config.ttsEngine ?? "kokoro",
      preset_voice_id: config.ttsModel,
      default_engine: config.ttsEngine ?? "kokoro",
    }),
  });
  if (!createRes.ok) {
    throw new SidecarError(`Voicebox profile create failed (${createRes.status})`);
  }
  const created = (await createRes.json()) as { id: string };
  profileIdCache.set(cacheKey, created.id);
  return created.id;
}

const VOICE_BOX_TERMINAL_STATUSES = new Set(["completed", "failed", "not_found"]);

async function waitForVoiceboxGeneration(
  baseUrl: string,
  generationId: string,
  timeoutMs = 1_800_000,
): Promise<void> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const res = await fetch(`${baseUrl}/generate/${generationId}/status`);
    if (!res.ok) {
      throw new SidecarError(`TTS status failed (${res.status})`);
    }
    const payload = parseSseStatusPayload(await res.text());
    if (payload.status === "completed") return;
    if (payload.status === "failed" || payload.status === "not_found") {
      throw new SidecarError(payload.error ?? `TTS ${payload.status ?? "failed"}`);
    }
    if (payload.status && !VOICE_BOX_TERMINAL_STATUSES.has(payload.status)) {
      await sleep(2_000);
      continue;
    }
    await sleep(2_000);
  }
  throw new SidecarError("TTS timed out waiting for Voicebox generation");
}

async function transcribeSpeaches(config: PttSidecarConfig, blob: Blob): Promise<string> {
  const form = new FormData();
  form.append("file", blob, "utterance.webm");
  form.append("model", config.sttModel);
  form.append("response_format", "json");
  if (config.sttPrompt) form.append("prompt", config.sttPrompt);

  const res = await fetch(`${config.baseUrl}/v1/audio/transcriptions`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    throw new SidecarError(`Transcribe failed (${res.status})`);
  }
  const data = (await res.json()) as { text?: string };
  return (data.text ?? "").trim();
}

function voiceboxDownloadMessage(status: number, body: unknown): string | null {
  if (status !== 202 && status !== 400) return null;
  if (typeof body !== "object" || body === null) return null;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === "string" && detail.includes("downloaded")) return detail;
  if (typeof detail === "object" && detail !== null) {
    const message = (detail as { message?: string }).message;
    if (message) return message;
  }
  return null;
}

async function transcribeVoicebox(config: PttSidecarConfig, blob: Blob): Promise<string> {
  const maxAttempts = 120;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const form = new FormData();
    form.append("file", blob, "utterance.webm");
    form.append("model", config.sttModel);
    if (config.sttPrompt) {
      form.append("initial_prompt", config.sttPrompt);
      form.append("prompt", config.sttPrompt);
    }

    const res = await fetch(`${config.baseUrl}/transcribe`, { method: "POST", body: form });
    if (res.status === 202) {
      await sleep(5_000);
      continue;
    }
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const downloadMsg = voiceboxDownloadMessage(res.status, body);
      throw new SidecarError(downloadMsg ?? `Transcribe failed (${res.status})`);
    }
    const data = (await res.json()) as { text?: string };
    return (data.text ?? "").trim();
  }
  throw new SidecarError("Whisper model still downloading — try again shortly");
}

async function transcribeAws(): Promise<string> {
  throw new SidecarError("AWS voice uses live streaming; restart the PTT session");
}

async function speakSpeaches(config: PttSidecarConfig, text: string): Promise<void> {
  const res = await fetch(`${config.baseUrl}/v1/audio/speech`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: config.ttsModel,
      input: text,
      response_format: "wav",
    }),
  });
  if (!res.ok) {
    throw new SidecarError(`TTS failed (${res.status})`);
  }
  await playAudioBuffer(await res.arrayBuffer());
}

async function fetchVoiceboxAudio(
  baseUrl: string,
  generationId: string,
): Promise<ArrayBuffer> {
  const audioRes = await fetch(`${baseUrl}/audio/${generationId}`);
  if (!audioRes.ok) {
    throw new SidecarError(`TTS audio failed (${audioRes.status})`);
  }
  return audioRes.arrayBuffer();
}

async function speakVoicebox(config: PttSidecarConfig, text: string): Promise<void> {
  const profileId = await ensureVoiceboxProfile(config);
  const profileName = config.ttsProfileName ?? "og-orm-ptt";
  const engine = config.ttsEngine ?? "kokoro";
  const speakBody = JSON.stringify({ text, profile: profileName, engine, language: "en" });
  const streamBody = JSON.stringify({
    profile_id: profileId,
    text,
    language: "en",
    engine,
  });

  const streamRes = await fetch(`${config.baseUrl}/generate/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: streamBody,
  });
  const streamType = streamRes.headers.get("content-type") ?? "";
  if (streamRes.ok && streamType.includes("audio")) {
    await playAudioBuffer(await streamRes.arrayBuffer());
    return;
  }

  const speakRes = await fetch(`${config.baseUrl}/speak`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Voicebox-Client-Id": "og-orm-ptt",
    },
    body: speakBody,
  });
  if (!speakRes.ok) {
    const body = await speakRes.json().catch(() => null);
    const downloadMsg = voiceboxDownloadMessage(speakRes.status, body);
    throw new SidecarError(downloadMsg ?? `TTS failed (${speakRes.status})`);
  }
  const gen = (await speakRes.json()) as { id: string };
  await waitForVoiceboxGeneration(config.baseUrl, gen.id);
  await playAudioBuffer(await fetchVoiceboxAudio(config.baseUrl, gen.id));
}

async function speakAws(config: PttSidecarConfig, text: string): Promise<void> {
  const res = await fetch(`${config.baseUrl}/speech`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) {
    throw new SidecarError(`AWS Polly TTS failed (${res.status})`);
  }
  await playAudioBuffer(await res.arrayBuffer());
}

export async function transcribeAudio(config: PttSidecarConfig, blob: Blob): Promise<string> {
  if (config.provider === "aws") return transcribeAws();
  if (config.provider === "voicebox") return transcribeVoicebox(config, blob);
  return transcribeSpeaches(config, blob);
}

export async function speakText(config: PttSidecarConfig, text: string): Promise<void> {
  if (config.provider === "aws") {
    await speakAws(config, text);
    return;
  }
  if (config.provider === "voicebox") {
    await speakVoicebox(config, text);
    return;
  }
  await speakSpeaches(config, text);
}
